import React, { useMemo } from 'react';
import { Button, Typography } from '@material-ui/core';
import { useTranslate } from 'react-admin';
import { InfoBox } from './InfoBox';
import { AllNoneButtons } from './shared/AllNoneButtons';

interface RowOption {
  name: string;
  label: string;
}

interface RowFilterProps {
  rows: RowOption[];
  activeRows: Set<string>;
  onToggleRow: (rowId: string) => void;
  onSelectAll: () => void;
  onClearAll: () => void;
}

export const RowFilter: React.FC<RowFilterProps> = ({
  rows,
  activeRows,
  onToggleRow,
  onSelectAll,
  onClearAll
}) => {
  const translate = useTranslate();
  const [displayMode, setDisplayMode] = React.useState<'name' | 'label'>('name');

  const getSortKey = (row: RowOption): string => {
    if (displayMode === 'label') {
      return row.label || row.name;
    }
    return row.name;
  };

  const sortedRows = useMemo(
    () =>
      [...rows].sort((a, b) =>
        getSortKey(a).localeCompare(getSortKey(b), undefined, { numeric: true, sensitivity: 'base' })
      ),
    [rows, displayMode]
  );

  const renderValue = (row: RowOption): string => {
    if (displayMode === 'label' && row.label) {
      return row.label;
    }
    return row.name;
  };

  const titleContent = (
    <>
      <Typography variant="subtitle2" component="span" style={{ fontWeight: 'bold' }}>
        {translate('osfc.storage_ui.row_filter.title')}
      </Typography>
      <div style={{ display: 'flex', gap: '4px' }}>
        <Button
          size="small"
          variant={displayMode === 'name' ? 'contained' : 'outlined'}
          color="secondary"
          onClick={() => setDisplayMode('name')}
        >
          {translate('osfc.storage_ui.row_filter.name')}
        </Button>
        <Button
          size="small"
          variant={displayMode === 'label' ? 'contained' : 'outlined'}
          color="secondary"
          onClick={() => setDisplayMode('label')}
        >
          {translate('osfc.storage_ui.row_filter.label')}
        </Button>
      </div>
    </>
  );

  return (
    <InfoBox title={titleContent} style={{ margin: '15px 0' }}>
      <Typography component="p" variant="body2" style={{ margin: '0 0 8px 0' }}><strong>{translate('osfc.storage_ui.row_filter.show_rows')}</strong></Typography>
      <div style={{ margin: '10px 0', display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
        {sortedRows.map(row => (
          <Button
            key={row.name}
            onClick={() => onToggleRow(row.name)}
            variant={activeRows.has(row.name) ? 'contained' : 'outlined'}
            color="secondary"
            size="small"
            style={{ minWidth: 'auto', padding: '5px 8px' }}
          >
            {renderValue(row)}
          </Button>
        ))}
      </div>
      <AllNoneButtons onSelectAll={onSelectAll} onClearAll={onClearAll} />
    </InfoBox>
  );
};
